import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import type { RecordSubscription } from 'pocketbase';
import { pb } from '../../../shared/api/pocketbase';
import { logAction } from '../../../shared/lib/userLogger';
import { Lead, HistoryEntry, ActionHistoryEntry, LeadFilesRecord, DocType } from './types';

export interface LeadsFilters {
  userIds?: string[];
  city?: string;
  boardIds?: string[];
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
  searchTerm?: string;
  noJobs?: boolean;
  tagIds?: string[];
  sources?: string[];
  types?: DocType[];
}

export type ColumnRealtimeHandler = (action: string, lead: Lead) => void;

const esc = (v: string) => v.replace(/"/g, '');
const eq = (field: string, val: string) => `${field} = "${esc(val)}"`;
const has = (field: string, val: string) => `${field} ~ "${esc(val)}"`;
const anyOf = (exprs: string[]) => exprs.length === 1 ? exprs[0] : `(${exprs.join(' || ')})`;

const buildPhonePart = (digits: string, term: string): string => {
  if (digits.length >= 10) {
    const core = digits.length === 11 ? digits.slice(1) : digits.slice(0, 10);
    const fmt = `+7 (${core.slice(0, 3)}) ${core.slice(3, 6)}-${core.slice(6, 8)}-${core.slice(8, 10)}`;
    return eq('phone', fmt);
  }
  return has('phone', digits.length >= 3 ? digits : term);
};

// Кастомный query-параметр для фильтра по типам документов: ?types=ticket,passport (обрабатывает бек)
const typesQuery = (filters?: LeadsFilters): Record<string, string> | undefined =>
  filters?.types?.length ? { types: filters.types.join(',') } : undefined;

const buildLeadsFilter = (filters?: LeadsFilters): string | undefined => {
  if (!filters) return undefined;
  const parts: string[] = [];

  if (filters.userIds?.length)  parts.push(anyOf(filters.userIds.map(id => eq('userId', id))));
  if (filters.city)             parts.push(eq('city', filters.city));
  if (filters.boardIds?.length) parts.push(anyOf(filters.boardIds.map(id => eq('boardId', id))));
  if (filters.createdFrom)      parts.push(`created >= "${esc(filters.createdFrom)} 00:00:00"`);
  if (filters.createdTo)        parts.push(`created <= "${esc(filters.createdTo)} 23:59:59"`);
  if (filters.updatedFrom)      parts.push(`updated >= "${esc(filters.updatedFrom)} 00:00:00"`);
  if (filters.updatedTo)        parts.push(`updated <= "${esc(filters.updatedTo)} 23:59:59"`);
  if (filters.noJobs)           parts.push('statusJobs.open = 0');
  if (filters.tagIds?.length)   parts.push(anyOf(filters.tagIds.map(id => has('tags', id))));
  if (filters.sources?.length)  parts.push(anyOf(filters.sources.map(s => eq('source', s))));
  // types отправляется отдельным query-параметром ?types=..., а не через filter — см. typesQuery()

  if (filters.searchTerm) {
    const term = esc(filters.searchTerm);
    // SQLite `~` (LIKE) регистронезависим только для латиницы — для кириллицы генерируем варианты регистра
    const lower = term.toLocaleLowerCase('ru');
    const upper = term.toLocaleUpperCase('ru');
    const title = lower.replace(/(^|\s)(\p{L})/gu, (_, s, c) => s + c.toLocaleUpperCase('ru'));
    const titlePart = anyOf([...new Set([term, lower, upper, title])].map(v => has('title', v)));
    const phonePart = buildPhonePart(term.replace(/\D/g, ''), term);
    parts.push(`(${titlePart} || ${has('id', term)} || ${phonePart})`);
  }

  return parts.join(' && ') || undefined;
};

const LEAD_EXPAND = 'userId,partnerId,tags';
const LEAD_FIELDS = '*,expand.userId.id,expand.userId.name,expand.userId.email,expand.partnerId.id,expand.partnerId.name,expand.tags.id,expand.tags.name,expand.tags.colors';

interface LeadsContextValue {
  leads: Lead[];
  isLoading: boolean;
  fetchLeadsTable: (filters: LeadsFilters | undefined, page: number) => Promise<{ items: Lead[]; totalItems: number }>;
  fetchLeadsPage: (boardId: string, page: number, perPage: number, filters?: LeadsFilters) => Promise<{ items: Lead[]; totalItems: number }>;
  subscribeColumnUpdates: (boardId: string, handler: ColumnRealtimeHandler) => () => void;
  createLead: (data: Partial<Lead>) => Promise<Lead>;
  updateLead: (id: string, data: Partial<Lead>) => Promise<void>;
  bulkUpdateLeads: (ids: string[], data: Partial<Lead>) => Promise<void>;
  deleteLead: (id: string) => Promise<void>;
  getLeadHistory: (leadId: string) => Promise<HistoryEntry[]>;
  getLeadActions: (leadId: string) => Promise<ActionHistoryEntry[]>;
  addHistoryEntry: (leadId: string, text: string) => Promise<void>;
  getLeadFiles: (leadId: string) => Promise<LeadFilesRecord | null>;
  uploadLeadFiles: (leadId: string, files: File[]) => Promise<LeadFilesRecord>;
  deleteLeadFile: (leadId: string, filename: string) => Promise<LeadFilesRecord | null>;
  setLeadFileTypes: (leadId: string, types: Record<string, DocType>) => Promise<LeadFilesRecord>;
}

const LeadsContext = createContext<LeadsContextValue | null>(null);

export const LeadsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const columnHandlersRef = useRef<Map<string, ColumnRealtimeHandler>>(new Map());
  const leadsRef = useRef<Lead[]>(leads);
  leadsRef.current = leads;
  useEffect(() => {
    const realtimeHandler = async (e: RecordSubscription<Lead>) => {
      if (e.action === 'delete') {
        setLeads(prev => prev.filter(l => l.id !== e.record.id));
        columnHandlersRef.current.forEach(handler => handler('delete', e.record as Lead));
        return;
      }
      try {
        const hydrated = await pb.collection('tasks').getOne<Lead>(e.record.id, {
          expand: LEAD_EXPAND,
          fields: LEAD_FIELDS,
        });
        if (e.action === 'create') {
          // Колонки не регистрируют хендлеры в режиме поиска/фильтра — не инжектируем чужие карточки
          if (columnHandlersRef.current.size > 0) {
            setLeads(prev => prev.some(l => l.id === hydrated.id) ? prev : [hydrated, ...prev]);
          }
        } else {
          setLeads(prev => prev.map(l => l.id === hydrated.id ? hydrated : l));
        }
        columnHandlersRef.current.forEach(handler => handler(e.action, hydrated));
      } catch {
        // запись была удалена до завершения запроса
      }
    };

    pb.collection('tasks').subscribe<Lead>('*', realtimeHandler);

    return () => {
      pb.collection('tasks').unsubscribe('*', realtimeHandler);
    };
  }, []);

  const fetchLeadsPage = useCallback(async (
    boardId: string,
    page: number,
    perPage: number,
    filters?: LeadsFilters,
  ): Promise<{ items: Lead[]; totalItems: number }> => {
    const userFilter = buildLeadsFilter(filters);
    const filter = userFilter ? `boardId = "${boardId}" && (${userFilter})` : `boardId = "${boardId}"`;
    const types = typesQuery(filters);
    // POST, а не getList: длинные фильтры не влезают в URL и роняли бек
    const result = await pb.send<{ items: Lead[]; totalItems: number }>('/api/tasks/filter', {
      method: 'POST',
      query: types,
      body: { filter, sort: '-updated', page, perPage, expand: LEAD_EXPAND, fields: LEAD_FIELDS },
      requestKey: `col-${boardId}-${page}-${userFilter ?? ''}-${types?.types ?? ''}`,
    });
    return { items: result.items, totalItems: result.totalItems };
  }, []);

  const subscribeColumnUpdates = useCallback((boardId: string, handler: ColumnRealtimeHandler) => {
    columnHandlersRef.current.set(boardId, handler);
    return () => { columnHandlersRef.current.delete(boardId); };
  }, []);

  const fetchLeadsTable = useCallback(async (
    filters: LeadsFilters | undefined,
    page: number,
  ): Promise<{ items: Lead[]; totalItems: number }> => {
    const result = await pb.send<{ items: Lead[]; totalItems: number }>('/api/tasks/filter', {
      method: 'POST',
      query: typesQuery(filters),
      body: { filter: buildLeadsFilter(filters) ?? '', sort: '-updated', page, perPage: 30, expand: LEAD_EXPAND, fields: LEAD_FIELDS },
      requestKey: null,
    });
    return { items: result.items, totalItems: result.totalItems };
  }, []);


  const createLead = useCallback(async (data: Partial<Lead>) => {
    const created = await pb.collection('tasks').create<Lead>({
      ...data,
      statusJobs: { open: 0, exp: 0, comp: 0 },
      actual: true,
    });

    let hydrated: Lead = created;
    try {
      hydrated = await pb.collection('tasks').getOne<Lead>(created.id, {
        expand: LEAD_EXPAND,
        fields: LEAD_FIELDS,
      });
    } catch (err) {
      console.error('Failed to hydrate created lead:', err);
    }

    setLeads(prev => [hydrated, ...prev]);
    logAction(`Created lead: ${hydrated.title || hydrated.id}`, 'ADD');

    return hydrated;
  }, []);

  const updateLead = useCallback(async (id: string, data: Partial<Lead>) => {
    await pb.collection('tasks').update<Lead>(id, data, { requestKey: null });
    const hydrated = await pb.collection('tasks').getOne<Lead>(id, {
      expand: LEAD_EXPAND,
      fields: LEAD_FIELDS,
      requestKey: null,
    });

    setLeads(prev => prev.map(item => (item.id === id ? hydrated : item)));
    columnHandlersRef.current.forEach(handler => handler('update', hydrated));
    logAction(`Updated lead: ${hydrated.title || id}`, 'EDIT');
  }, []);

  const bulkUpdateLeads = useCallback(async (ids: string[], data: Partial<Lead>) => {
    if (!ids.length) return;
    const batch = pb.createBatch();
    ids.forEach(id => batch.collection('tasks').update(id, data, { requestKey: null }));
    await batch.send();
    logAction(`Bulk updated ${ids.length} leads`, 'EDIT');
  }, []);

  const deleteLead = useCallback(async (id: string) => {
    await pb.collection('tasks').delete(id);
    setLeads(prev => prev.filter(item => item.id !== id));
    logAction(`Deleted lead: ${id}`, 'REMOVE');
  }, []);

  const getLeadHistory = useCallback(async (leadId: string): Promise<HistoryEntry[]> => {
    return pb.collection('comments').getFullList<HistoryEntry>({
      filter: `taskId = "${leadId}"`,
      sort: '-created',
      expand: 'userId',
      fields: 'id,taskId,text,created,expand.userId.id,expand.userId.name',
    });
  }, []);

  const getLeadActions = useCallback(async (leadId: string): Promise<ActionHistoryEntry[]> => {
    return pb.collection('history').getFullList<ActionHistoryEntry>({
      filter: `taskId = "${leadId}"`,
      sort: '-created',
      expand: 'userId',
      fields: 'id,taskId,action,created,expand.userId.id,expand.userId.name',
      requestKey: null,
    });
  }, []);

  const addHistoryEntry = useCallback(async (leadId: string, text: string) => {
    await pb.collection('comments').create<HistoryEntry>({
      taskId: leadId,
      userId: pb.authStore.model?.id ?? '',
      text,
    });
  }, []);

  const getLeadFiles = useCallback(async (leadId: string): Promise<LeadFilesRecord | null> => {
    const records = await pb.collection('files').getFullList<LeadFilesRecord>({
      filter: `taskId = "${leadId}"`,
      sort: '-created',
      fields: 'id,taskId,userId,types,images,collectionId,collectionName',
    });

    return records[0] ?? null;
  }, []);

  const uploadLeadFiles = useCallback(async (leadId: string, files: File[]): Promise<LeadFilesRecord> => {
    const existing = await pb.collection('files').getFullList<LeadFilesRecord>({
      filter: `taskId = "${leadId}"`,
      sort: '-created',
      fields: 'id',
    }).then(records => records[0] ?? null);

    const fileInfo = files.map(f => ({ name: f.name, size: f.size, type: f.type }));
    logAction(`Uploaded ${files.length} file(s) to lead ${leadId}: ${files.map(f => f.name).join(', ')}`, 'ADD', { leadId, files: fileInfo });

    if (existing) {
      return pb.collection('files').update<LeadFilesRecord>(existing.id, {
        taskId: leadId,
        userId: pb.authStore.model?.id ?? '',
        'images+': files,
      });
    }

    return pb.collection('files').create<LeadFilesRecord>({
      taskId: leadId,
      userId: pb.authStore.model?.id ?? '',
      images: files,
    });
  }, []);

  const deleteLeadFile = useCallback(async (leadId: string, filename: string): Promise<LeadFilesRecord | null> => {
    const existing = await pb.collection('files').getFullList<LeadFilesRecord>({
      filter: `taskId = "${leadId}"`,
      sort: '-created',
      fields: 'id',
    }).then(records => records[0] ?? null);

    if (!existing) {
      return null;
    }

    return pb.collection('files').update<LeadFilesRecord>(existing.id, {
      'images-': [filename],
    });
  }, []);

  const setLeadFileTypes = useCallback(async (leadId: string, types: Record<string, DocType>): Promise<LeadFilesRecord> => {
    const existing = await pb.collection('files').getFullList<LeadFilesRecord>({
      filter: `taskId = "${leadId}"`,
      sort: '-created',
      fields: 'id',
    }).then(records => records[0] ?? null);

    if (existing) {
      return pb.collection('files').update<LeadFilesRecord>(existing.id, { types });
    }

    return pb.collection('files').create<LeadFilesRecord>({
      taskId: leadId,
      userId: pb.authStore.model?.id ?? '',
      types,
    });
  }, []);

  const value = React.useMemo<LeadsContextValue>(() => ({
    leads,
    isLoading,
    fetchLeadsTable,
    fetchLeadsPage,
    subscribeColumnUpdates,
    createLead,
    updateLead,
    bulkUpdateLeads,
    deleteLead,
    getLeadHistory,
    getLeadActions,
    addHistoryEntry,
    getLeadFiles,
    uploadLeadFiles,
    deleteLeadFile,
    setLeadFileTypes,
  }), [leads, isLoading, fetchLeadsTable, fetchLeadsPage, subscribeColumnUpdates, createLead, updateLead, bulkUpdateLeads, deleteLead, getLeadHistory, getLeadActions, addHistoryEntry, getLeadFiles, uploadLeadFiles, deleteLeadFile, setLeadFileTypes]);

  return React.createElement(LeadsContext.Provider, { value }, children);
};

export function useLeads(): LeadsContextValue {
  const ctx = useContext(LeadsContext);
  if (!ctx) throw new Error('useLeads must be used inside LeadsProvider');
  return ctx;
}

export async function fetchSourcesCollectionOptions(): Promise<{ items: { value: string; label: string; disabled?: boolean }[]; hasMore: boolean }> {
  const records = await pb.collection('sources').getFullList<{ id: string; name: string; disabled: boolean }>({ sort: 'name' });
  const items = records.map(r => ({ value: r.name, label: r.name, disabled: r.disabled || undefined }));
  return { items, hasMore: false };
}

export async function fetchSourceOptions(params: { page: number; search: string }): Promise<{ items: { value: string; label: string }[]; hasMore: boolean }> {
  const { page, search } = params;
  const safe = search.trim().replace(/"/g, '');
  const res = await pb.collection('unique_sources').getList<{ id: string; source: string }>(page, 100, {
    filter: safe ? `source ~ "${safe}"` : '',
    sort: 'source',
    requestKey: `sources-${page}-${safe}`,
  });
  const items = [...new Set(res.items.map(r => r.source?.trim()).filter(Boolean))]
    .map(s => ({ value: s as string, label: s as string }));
  return { items, hasMore: page < res.totalPages };
}

// Города для фильтра: страницами по tasks (нет отдельной коллекции), дедуп на клиенте.
// Первые 50 строк, догрузка при скролле, фильтр при поиске — без выкачивания всей коллекции.
export async function fetchCityOptions(params: { page: number; search: string }): Promise<{ items: { value: string; label: string }[]; hasMore: boolean }> {
  const { page, search } = params;
  const safe = search.trim().replace(/"/g, '');
  const filter = safe ? `city != "" && city ~ "${safe}"` : `city != ""`;
  const res = await pb.collection('tasks').getList<{ city?: string }>(page, 50, {
    filter,
    fields: 'city',
    sort: 'city',
    requestKey: `cities-${page}-${safe}`,
  });
  const items = [...new Set(res.items.map(r => r.city?.trim()).filter(Boolean))]
    .map(c => ({ value: c as string, label: c as string }));
  return { items, hasMore: page < res.totalPages };
}
